import { FetchPolicy, useLazyQuery, useMutation } from '@apollo/client';
import * as React from 'react';
import {
    CREATE_CLASSROOM,
    CreateClassroomResponse,
    CreateClassroomVariables,
    GET_ONGOING_CLASSROOMS,
    GetOngoingClassroomsResponse,
    GetOngoingClassroomsVariables,
    PUBLISH_LESSON,
    PublishLessonResponse,
    PublishLessonVariables,
    UPDATE_CLASSROOM,
    UpdateClassroomVariables,
} from './gql';
import { Classroom } from '@/types/classroom';
import { Lesson } from '@/models/lesson';
import { Class } from '@/models/class';
import { useBroadcast } from 'use-broadcast-ts';

export const useClassroom = () => {
    const { send } = useBroadcast<{ type: string; classroomId: string }>('classroom_channel');

    // gql
    const [queryOngoingClassrooms] = useLazyQuery<GetOngoingClassroomsResponse, GetOngoingClassroomsVariables>(GET_ONGOING_CLASSROOMS, {
        fetchPolicy: 'network-only',
    });
    const [updateClassroom] = useMutation<undefined, UpdateClassroomVariables>(UPDATE_CLASSROOM);
    const [publishLesson] = useMutation<PublishLessonResponse, PublishLessonVariables>(PUBLISH_LESSON);
    const [createClassroom] = useMutation<CreateClassroomResponse, CreateClassroomVariables>(CREATE_CLASSROOM);

    // callbacks
    const fetch = React.useCallback(
        async (filter: 'ongoing', fetchPolicy?: FetchPolicy) => {
            if (filter === 'ongoing') {
                const now = new Date();
                const classrooms = await queryOngoingClassrooms({ variables: { now: now.toISOString() }, fetchPolicy: fetchPolicy });
                return classrooms.data?.testrooms;
            }
            return undefined;
        },
        [queryOngoingClassrooms],
    );

    const start = React.useCallback(
        async (lesson: Lesson, cl: Class, classroomName: string, passlock?: string): Promise<Classroom> => {
            try {
                const publishedLessson = await publishLesson({ variables: { lessonID: lesson.id } });
                if (!publishedLessson.data) {
                    throw new Error('Failed to publish lesson');
                }
                const createClassroomResponse = await createClassroom({ variables: { name: classroomName, lessonVersion: publishedLessson.data?.publishLesson.id, class: cl.id, passlock } });

                if (!createClassroomResponse.data) {
                    throw new Error(`Failed to create classroom`);
                }

                send({ type: 'started', classroomId: createClassroomResponse.data.createTestroom.id });

                return createClassroomResponse.data.createTestroom;
            } catch (error) {
                return Promise.reject(new Error(`Failed to start lesson: ${error}`));
            }
        },
        [publishLesson, createClassroom, send],
    );

    const terminate = React.useCallback(
        async (classroom: Classroom) => {
            await updateClassroom({
                variables: {
                    where: {
                        id: classroom.id,
                    },
                    data: {},
                },
            });

            send({ type: 'terminated', classroomId: classroom.id });
        },
        [updateClassroom, send],
    );

    return {
        fetch,
        start,
        terminate,
    };
};
